/**
 * Apps — the low/no-code application layer (ADR-0010 P4).
 *
 * Lists every app (a bundle of Data Tables + — later — Views, Forms,
 * Pages). Create spins up an app + its isolated `tray_data_*` schema
 * via the backend `DataTableDDL` service. Row click drills into the
 * app detail (table designer + data grid).
 *
 * Slug is the stable, URL-safe identifier; it's auto-suggested from the
 * name but editable. Everything is tag-scoped on the `apps` RBAC
 * resource, so a non-admin only sees apps their tags grant.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppWindow, Plus, Table2, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DescriptionTooltip } from "@/components/ui/description-tooltip";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { appsApi, type AppCreate } from "@/lib/api-apps";
import { usePermissions } from "@/lib/permissions";


/** Lower-case, hyphenate, strip non-url-safe chars — a friendly slug
 *  suggestion. The user can still override; the backend validates.
 *
 *  Separator is **underscore**, not hyphen, to match the backend slug
 *  rule (`^[a-z][a-z0-9_]*$` — letters/digits/underscore only). A leading
 *  underscore/digit run is stripped so the slug starts with a letter
 *  whenever the name contains one. Used for app / role / form slugs. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "") // slug must start with a letter
    .replace(/_+$/g, "")
    .slice(0, 63);
}


export function AppsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can("apps", "write");
  const canDelete = can("apps", "delete");

  const [open, setOpen] = React.useState(false);

  const listQ = useQuery({
    queryKey: ["apps"],
    queryFn: () => appsApi.list(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => appsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps"] }),
  });

  const apps = listQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("apps.title", "Apps")}
        description={t(
          "apps.subtitle",
          "Low-code applications: Data Tables, views and workflows over data you own.",
        )}
        actions={
          canWrite && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("apps.new", "New app")}
            </Button>
          )
        }
      />
      <PageContent>
        {apps.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={AppWindow}
            title={t("apps.empty", "No apps yet")}
            description={t(
              "apps.emptyDescription",
              "Create your first app to start modelling Data Tables and building on top of them.",
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>{t("apps.name", "Name")}</Th>
                  <Th className="w-40">{t("apps.slug", "Slug")}</Th>
                  <Th className="w-24 text-center">{t("apps.tables", "Tables")}</Th>
                  <Th className="w-20 text-center">{t("apps.status", "Status")}</Th>
                  {canDelete && <Th className="w-20 text-right">{t("common.actions", "Actions")}</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {apps.map((a) => (
                  <Tr
                    key={a.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/apps/${a.id}`)}
                  >
                    <Td>
                      <div className="flex items-center gap-2 font-medium">
                        <AppWindow className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                        {a.name}
                        <DescriptionTooltip text={a.description} />
                      </div>
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground">{a.slug}</Td>
                    <Td className="text-center">
                      <span className="inline-flex items-center gap-1 text-sm">
                        <Table2 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                        {a.tables_count}
                      </span>
                    </Td>
                    <Td className="text-center">
                      <Badge tone={a.enabled ? "active" : "neutral"}>
                        {a.enabled ? t("apps.enabled", "on") : t("apps.disabled", "off")}
                      </Badge>
                    </Td>
                    {canDelete && (
                      <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("common.delete", "Delete")}
                          onClick={() => {
                            if (
                              confirm(
                                t("apps.confirmDelete", 'Delete app "{{name}}" and ALL its data?', {
                                  name: a.name,
                                }),
                              )
                            ) {
                              remove.mutate(a.id);
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                        </Button>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>

      <CreateAppDialog open={open} onOpenChange={setOpen} />
    </PageContainer>
  );
}


function CreateAppDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [description, setDescription] = React.useState("");

  // Reset on open so a re-open starts clean.
  React.useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugTouched(false);
      setDescription("");
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (body: AppCreate) => appsApi.create(body),
    onSuccess: (app) => {
      qc.invalidateQueries({ queryKey: ["apps"] });
      onOpenChange(false);
      navigate(`/apps/${app.id}`);
    },
  });

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const canSubmit = name.trim().length > 0 && effectiveSlug.length > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.createTitle", "New app")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="app-name">{t("apps.name", "Name")}</Label>
            <Input
              id="app-name"
              value={name}
              autoFocus
              placeholder="Ticketing"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="app-slug">{t("apps.slug", "Slug")}</Label>
            <Input
              id="app-slug"
              value={effectiveSlug}
              className="font-mono"
              placeholder="ticketing"
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugify(e.target.value));
              }}
            />
            <p className="text-[11px] text-muted-foreground">
              {t("apps.slugHint", "URL-safe id, used for the app's data schema. Can't change easily later.")}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="app-desc">{t("apps.description", "Description")}</Label>
            <Input
              id="app-desc"
              value={description}
              placeholder={t("common.optional", "Optional")}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {create.isError && (
            <Alert tone="danger" title={t("apps.createFailed", "Could not create app")}>
              <span className="font-mono text-xs">{(create.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() =>
              create.mutate({
                name: name.trim(),
                slug: effectiveSlug,
                description: description.trim() || null,
              })
            }
          >
            {t("apps.create", "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
